import { useState } from "react";
import { MailWarning } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Slim banner shown at the top of the storefront when a logged-in customer's
// email is not yet verified. Admins never see it.
export default function VerifyBanner() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!user || typeof user !== "object") return null;
  if (user.role === "admin") return null;
  if (user.email_verified) return null;
  if (dismissed) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent. Check your inbox.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="w-full text-white text-xs relative z-[60]"
      style={{ background: "#2a1e1a" }}
      data-testid="verify-banner"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <MailWarning size={14} className="shrink-0" style={{ color: "#d4b98c" }} />
          <p className="truncate">
            Please verify <span className="mono">{user.email}</span> to place orders.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={resend}
            disabled={sending}
            className="overline tracking-[0.22em] underline underline-offset-4 disabled:opacity-50"
            data-testid="verify-banner-resend"
          >
            {sending ? "Sending…" : "Resend email"}
          </button>
          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="opacity-60 hover:opacity-100 leading-none"
            data-testid="verify-banner-dismiss"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
